import { useEffect, useState } from "react";

import { subscribeToCable } from "../lib/cable";
import type { GenerationOutputUpdate } from "../types";

type OutputUpdateMessage = GenerationOutputUpdate & {
  event: "output_update";
};

type OutputStepMessage = {
  event: "output_step";
  output_id: number;
  step: string;
};

type CableMessage = OutputUpdateMessage | OutputStepMessage;

export default function useGenerationSocket(jobId: number | null, initialOutputs: GenerationOutputUpdate[]) {
  const initialOutputsKey = JSON.stringify(initialOutputs);
  const [outputs, setOutputs] = useState<Map<number, GenerationOutputUpdate>>(() => {
    const m = new Map<number, GenerationOutputUpdate>();
    initialOutputs.forEach((o) => m.set(o.output_id, o));
    return m;
  });

  useEffect(() => {
    setOutputs((prev) => {
      const next = new Map(prev);
      initialOutputs.forEach((output) => {
        next.set(output.output_id, { ...next.get(output.output_id), ...output });
      });
      return next;
    });
  }, [initialOutputsKey]);

  useEffect(() => {
    if (!jobId) return;

    return subscribeToCable<CableMessage>({
      channel: "GenerationChannel",
      params: { job_id: jobId },
      onMessage: (data) => {
        if (!data) return;

        if (data.event === "output_step") {
          setOutputs((prev) => {
            const existing = prev.get(data.output_id);
            if (!existing) return prev;
            const next = new Map(prev);
            next.set(data.output_id, { ...existing, step: data.step });
            return next;
          });
          return;
        }

        if (data.event !== "output_update") return;
        setOutputs((prev) => {
          const next = new Map(prev);
          const existing = prev.get(data.output_id);
          next.set(data.output_id, {
            output_id: data.output_id,
            mode: data.mode,
            status: data.status,
            image_url: data.image_path
              ? window.location.origin + data.image_path
              : existing?.image_url,
            prompt_used: data.prompt_used ?? existing?.prompt_used,
            error_message: data.error_message ?? existing?.error_message,
            step: data.status === "running" ? existing?.step : undefined,
            fidelity_score: data.fidelity_score ?? existing?.fidelity_score,
            marketing_score: data.marketing_score ?? existing?.marketing_score,
            qa_verdict: data.qa_verdict ?? existing?.qa_verdict,
          });
          return next;
        });
      },
    });
  }, [jobId]);

  return Array.from(outputs.values());
}
